import type {
  CurrentUser,
  DestinationProgress,
  GeoServer,
  LangueParlee,
  NotificationSettings,
  Post,
  PrivacySettings,
  PublicUser,
  SiroccoEvent,
} from '@sirocco/shared';
import { onDisconnect, onValue, push, ref, remove, set } from 'firebase/database';
import type { PublicProfileResponse, SettingsResponse } from '../api/endpoints';
import { db } from './app';
import { logout, requireUid } from './auth';
import { isBlocked, isHidden, listBlocked } from './blocks';
import { appeler } from './callable';
import { listMine } from './events';
import { friendshipStatus } from './friends';
import { uploadIfDataUrl } from './media';
import { normalizeUsername, paths } from './paths';
import { listByAuthor } from './posts';
import {
  DEFAULT_NOTIFICATIONS,
  DEFAULT_PRIVACY,
  ONLINE_WINDOW_MS,
  toCurrentUser,
  toPublicUser,
  type UserRecord,
} from './records';
import { retenirFuseau } from '../utils/spotTime';
import {
  readKeys,
  readList,
  readOptionalValue,
  readValue,
  SiroccoDataError,
  updateValues,
  writeValue,
} from './rtdb';
import { AUTRES_LANGUES, t, langue, type AutreLangue } from '../i18n';
import { versCarte, versListe } from '../utils/langues';

/**
 * Profils, reglages et catalogue des spots.
 *
 * Sans serveur, c'est ici que la confidentialite est appliquee : chaque
 * lecture d'un profil tiers passe par `privacy` avant de composer la
 * reponse, exactement comme le faisait l'API.
 */

/** Une destination, telle qu'elle est rangee dans `users/{uid}/destinations`. */
interface DestinationRecord {
  /** Le nom du spot en francais, qui fait foi : d'autres membres le lisent. */
  serverName: string;
  /** Le meme dans les autres langues, pour qui lit l'application dans l'une d'elles. */
  serverNames?: Partial<Record<AutreLangue, string>>;
  points: number;
  trophiesUnlocked: number;
  trophiesTotal: number;
  accent: string;
  /** Un serveur sans trophees : le profil n'y montre ni points ni progression. */
  sansTrophees?: boolean;
}

/** Profil du membre connecte, ou une erreur claire si le compte a disparu. */
async function myRecord(uid: string): Promise<UserRecord> {
  const record = await readValue<UserRecord>(paths.user(uid));
  if (!record) throw new SiroccoDataError(t.profil.erreurs.compteIntrouvable, 'not-found');
  return record;
}

/* ------------------------------------------------------------------ */
/* Mon profil                                                          */
/* ------------------------------------------------------------------ */

/**
 * Tout l'ecran Profil en un appel : identite, destinations, derniers
 * posts et prochains evenements. Les trois lectures sont independantes,
 * donc menees de front.
 */
export async function myProfile(): Promise<{
  user: CurrentUser;
  destinations: DestinationProgress[];
  posts: Post[];
  events: SiroccoEvent[];
  stats: { totalPoints: number; level: number; cities: number };
}> {
  const uid = requireUid();
  const [record, posts, events] = await Promise.all([myRecord(uid), listByAuthor(uid), listMine()]);

  const user = toCurrentUser(uid, record);
  return {
    user,
    destinations: user.destinations,
    posts: posts.slice(0, 3),
    /*
      Ce que je rejoins et ce que j'organise, ensemble.

      Le profil ne montrait que `joined`, et ces deux listes sont
      disjointes : la sortie qu'on vient de creer n'apparaissait donc
      nulle part sur son propre profil, alors qu'on y est inscrit et que
      le titre au-dessus dit « Mes evenements et sorties ». L'ecran
      complet, lui, les donne bien toutes deux, sous deux onglets.

      Triees par date : trois places seulement, autant qu'elles aillent
      aux plus proches.
    */
    events: [...events.joined, ...events.organized]
      .sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`))
      .slice(0, 3),
    stats: {
      totalPoints: user.totalPoints,
      level: user.level,
      cities: user.destinations.length,
    },
  };
}

/**
 * Profil d'un autre membre.
 *
 * Ce sont ses reglages a lui qui decident de ce qu'on lit : filtrer a
 * l'affichage laisserait la donnee transiter, ce qui n'est pas un
 * masquage mais un decor.
 */
export async function publicProfile(uid: string): Promise<PublicProfileResponse> {
  const me = requireUid();
  const [record, status, blocked, hidden] = await Promise.all([
    readValue<UserRecord>(paths.user(uid)),
    friendshipStatus(uid),
    isBlocked(uid),
    isHidden(uid),
  ]);

  if (!record) throw new SiroccoDataError(t.profil.erreurs.membreIntrouvable, 'not-found');
  const user = toPublicUser(uid, record);
  const privacy = record.privacy ?? DEFAULT_PRIVACY;

  // Un membre bloque garde une fiche, mais vide : c'est ce qui permet de
  // revenir dessus pour le debloquer.
  /*
    Sauf pour soi-meme.

    On arrive sur sa propre fiche depuis la liste des participants d'une
    sortie — c'est la meme page que pour les autres, et c'est voulu. Mais
    un profil reserve a ses amis se serait ferme devant son proprietaire,
    qui n'est pas son propre ami.
  */
  const cestMoi = uid === me;
  if (!cestMoi && (blocked || (privacy.profileVisibility === 'friends' && status !== 'friends'))) {
    return {
      user,
      friendshipStatus: status,
      blocked,
      hidden,
      restricted: true,
      destinations: [],
      posts: [],
      events: [],
      eventsHidden: true,
    };
  }

  /*
    Le compte de l'application n'a ni destinations ni trophees.

    Il ne joue pas au jeu qu'il propose — c'est deja pourquoi il porte
    une coche a la place d'un medaillon de niveau. Une colonne de
    trophees sous son pseudo dirait le contraire, et « 0/12 trophees »
    ferait passer l'equipe pour un membre en retard. Rien n'est masque
    a l'ecran : il n'y a rien a lire, donc rien n'est lu.
  */
  if (record.officiel) {
    return {
      user,
      friendshipStatus: status,
      blocked: false,
      hidden,
      restricted: false,
      destinations: [],
      posts: [],
      events: [],
      eventsHidden: true,
    };
  }

  const posts = privacy.showPosts ? await listByAuthor(uid) : [];

  return {
    user,
    friendshipStatus: status,
    blocked: false,
    hidden,
    restricted: false,
    /* `showTrophies` ne commande plus qu'elles (« Mes destinations »), et elles peuvent en
       plus se reserver aux amis : voir `destinationsFriendsOnly`. */
    destinations:
      privacy.showTrophies && (!privacy.destinationsFriendsOnly || status === 'friends')
        ? toCurrentUser(uid, record).destinations
        : [],
    posts: posts.slice(0, 3),
    // Les participations d'un tiers ne sont pas indexees par membre :
    // l'ecran affiche la mention plutot qu'une liste incomplete.
    events: [],
    eventsHidden: !privacy.showEvents,
  };
}

/* ------------------------------------------------------------------ */
/* Modification du profil                                              */
/* ------------------------------------------------------------------ */

/**
 * Bio, pseudo, avatar.
 *
 * Le pseudo vit a deux endroits : le profil et l'index d'unicite. Les
 * deux bougent dans la meme mise a jour, sinon un pseudo pourrait
 * rester reserve par un compte qui ne le porte plus.
 */
export async function updateProfile(patch: {
  bio?: string;
  username?: string;
  avatarUrl?: string | null;
  languesParlees?: LangueParlee[];
}): Promise<CurrentUser> {
  const uid = requireUid();
  const record = await myRecord(uid);

  const updates: Record<string, unknown> = {};
  const next: UserRecord = { ...record };

  if (patch.bio !== undefined) {
    next.bio = patch.bio.trim();
    updates[paths.userField(uid, 'bio')] = next.bio;
  }

  if (patch.languesParlees !== undefined) {
    const langues = versListe(versCarte(patch.languesParlees));
    if (langues.length === 0) {
      throw new SiroccoDataError(t.commun.uneLangue, 'invalid', 'languesParlees');
    }
    next.languesParlees = versCarte(langues);
    updates[paths.userField(uid, 'languesParlees')] = next.languesParlees;
  }

  if (patch.avatarUrl !== undefined) {
    // La photo choisie part vers Storage ici : l'ecran ne connait qu'une
    // data URL, ce qui lui donne son apercu avant meme le reseau.
    const photo = patch.avatarUrl ? await uploadIfDataUrl(patch.avatarUrl) : null;
    next.avatarUrl = photo;
    updates[paths.userField(uid, 'avatarUrl')] = photo;
  }

  if (patch.username !== undefined) {
    const username = patch.username.trim();
    const nextKey = paths.username(username);
    const currentKey = paths.username(record.username);

    // Meme cle d'index : seule la casse ou les accents changent, il n'y
    // a donc rien a reserver ni a liberer.
    if (nextKey !== currentKey) {
      const owner = await readValue<string>(nextKey);
      if (owner && owner !== uid) {
        throw new SiroccoDataError(
          t.profil.erreurs.pseudoPris,
          'username-taken',
          'username',
        );
      }
      updates[currentKey] = null;
      updates[nextKey] = uid;
    }

    next.username = username;
    updates[paths.userField(uid, 'username')] = username;
  }

  if (Object.keys(updates).length > 0) await updateValues(updates);
  return toCurrentUser(uid, next);
}

/**
 * Mode nuit.
 * Range sur le profil pour suivre le compte d'un appareil a l'autre ;
 * l'application en garde une copie locale, qui evite l'eclair blanc au
 * demarrage avant que la session soit connue.
 */
export async function setNightMode(nightMode: boolean): Promise<CurrentUser> {
  const uid = requireUid();
  const record = await myRecord(uid);

  await writeValue(paths.userField(uid, 'nightMode'), nightMode);
  return toCurrentUser(uid, { ...record, nightMode });
}

/**
 * Signe de vie.
 *
 * `lastSeenAt` est la seule chose qui rende le statut "en ligne" vrai :
 * `toPublicUser` le compare a une fenetre de cinq minutes. Personne ne
 * l'ecrivait, donc la pastille verte ne s'allumait jamais. C'est la
 * session qui appelle ceci, au demarrage puis regulierement tant que
 * l'application est au premier plan.
 *
 * Une seule feuille est ecrite, jamais le profil entier : deux appareils
 * ouverts en meme temps ne doivent pas s'ecraser l'un l'autre.
 */
/**
 * Ce que ce telephone fait tourner, note a chaque ouverture.
 *
 * Rien ne le disait : le jour ou une version sort, personne ne peut savoir
 * qui l'a prise et qui attend encore — pas meme pour decider s'il faut
 * insister. La coquille est le numero qu'Apple connait ; l'interface est
 * celle que le moteur de mise a jour sert, et les deux peuvent differer.
 *
 * Un compte sans releve n'a pas rouvert l'application, ou tourne sur une
 * coquille anterieure a ce releve.
 */
export async function noterLaVersion(releve: {
  coquille: number;
  version: string;
  interface: string;
}): Promise<void> {
  const uid = requireUid();
  await updateValues({
    [paths.userField(uid, 'coquille')]: releve.coquille,
    [paths.userField(uid, 'version')]: releve.version,
    [paths.userField(uid, 'interfaceVue')]: releve.interface.slice(0, 8),
    [paths.userField(uid, 'versionVueLe')]: Date.now(),
  });
}

/**
 * La langue dans laquelle ce membre lit l'application.
 *
 * Les notifications et les courriels partent des fonctions, qui ne voient
 * pas le telephone : c'est d'ici qu'elles apprennent dans quelle langue
 * ecrire. La langue resolue, pas la preference : « automatique » ne dit
 * rien a un serveur.
 */
export async function retenirLaLangue(langue: string): Promise<void> {
  await writeValue(paths.userField(requireUid(), 'langue'), langue);
}

export async function touchLastSeen(): Promise<void> {
  await writeValue(paths.userField(requireUid(), 'lastSeenAt'), Date.now());
}

/**
 * Ma presence, tenue par le serveur.
 *
 * Le battement de coeur dit « vivant il y a peu » ; il ne sait pas dire
 * « parti ». Ici, chaque session ouverte depose une connexion sous
 * `users/{moi}/connections/{id}`, et confie au serveur l'ordre de
 * l'effacer si le lien casse (`onDisconnect`) : fermeture de
 * l'application, reseau coupe, telephone eteint — l'effacement part du
 * serveur, en secondes, sans que l'appareil ait rien a faire.
 *
 * Une connexion par session, pas une par membre : deux appareils
 * ouverts ne s'ecrasent pas, et le statut ne s'eteint que quand le
 * dernier s'en va. Le marqueur `presenceV2` dit aux lecteurs que ce
 * membre parle ce langage : sans lui, ils retombent sur l'age du
 * battement — c'est ce qui garde les anciens clients lisibles.
 *
 * Le masquage a aussi sa part : application cachee (`visibilitychange`),
 * la connexion se retire tout de suite — iOS peut garder le lien vivant
 * quelques secondes en arriere-plan, et « en ligne » mentirait.
 */
/**
 * La connexion de cette session, pour pouvoir la retirer d'ailleurs.
 *
 * Le nettoyage de l'effet React ne suffit pas a la deconnexion : voir
 * `retirerLaPresence`.
 */
let maConnexion: ReturnType<typeof push> | null = null;

/**
 * Retire ma presence, tant que la session vaut encore.
 *
 * Se deconnecter n'est pas partir : le lien reseau reste ouvert, donc
 * `onDisconnect` ne se declenche pas. Et le nettoyage de l'effet React
 * n'arrive qu'apres `signOut`, quand la regle refuse deja l'ecriture —
 * la connexion restait alors en base pour toujours, et son proprietaire
 * « en ligne » avec elle.
 *
 * D'ou cet appel explicite, a faire avant de fermer la session, comme
 * l'oubli de l'appareil et celui des appels.
 */
export async function retirerLaPresence(): Promise<void> {
  const mienne = maConnexion;
  if (!mienne) return;
  maConnexion = null;
  // L'ordre au serveur d'abord : sans quoi il effacerait plus tard un
  // noeud qui pourrait etre celui d'une session suivante.
  await onDisconnect(mienne).cancel().catch(() => undefined);
  await remove(mienne).catch(() => undefined);
}

export function signalerPresence(): () => void {
  const uid = requireUid();
  const mienne = push(ref(db(), paths.userField(uid, 'connections')));
  maConnexion = mienne;

  const surConnexion = onValue(ref(db(), '.info/connected'), (instantane) => {
    if (instantane.val() !== true) return;
    /*
      L'ordre au serveur d'abord, la presence ensuite : dans l'autre sens,
      un lien qui casse entre les deux laisserait une connexion orpheline,
      « en ligne » pour toujours.
    */
    void onDisconnect(mienne)
      .remove()
      .then(() => {
        if (document.visibilityState !== 'hidden') return set(mienne, true);
      })
      .catch(() => undefined);
  });

  const surVisibilite = () => {
    if (document.visibilityState === 'hidden') void remove(mienne).catch(() => undefined);
    else void set(mienne, true).catch(() => undefined);
  };
  document.addEventListener('visibilitychange', surVisibilite);

  void writeValue(paths.userField(uid, 'presenceV2'), true).catch(() => undefined);

  return () => {
    document.removeEventListener('visibilitychange', surVisibilite);
    surConnexion();
    if (maConnexion === mienne) maConnexion = null;
    void remove(mienne).catch(() => undefined);
  };
}

/**
 * « En ligne », mais vivant.
 *
 * `toPublicUser` fige le statut a l'instant du chargement : vrai a
 * l'ouverture d'une conversation, il le restait pour toujours — l'autre
 * pouvait partir, la fenetre expirer, rien ne le recalculait. C'etait le
 * defaut visible dans l'en-tete d'une discussion.
 *
 * Ici, deux ecoutes et un metronome. Les ecoutes portent sur les deux
 * feuilles qui fondent le statut — le signe de vie, et le reglage qui
 * autorise a le montrer. Le metronome rejuge toutes les trente secondes :
 * un depart ne s'ecrit nulle part, c'est l'age du dernier battement qui
 * finit par le dire.
 */
export function watchPresence(uid: string, surChangement: (enLigne: boolean) => void): () => void {
  let dernierVu: number | null = null;
  let visible = true;
  let connexions = 0;
  let v2 = false;
  let annonce: boolean | null = null;

  const juger = () => {
    /*
      Deux langages. Un membre qui parle `presenceV2` est en ligne tant
      qu'une de ses sessions tient — et hors ligne des que le serveur a
      efface la derniere, en secondes. Pour les autres — vitrine,
      anciennes installations — l'age du battement reste seul juge.
    */
    const vivant = v2
      ? connexions > 0
      : dernierVu !== null && Date.now() - dernierVu < ONLINE_WINDOW_MS;
    const enLigne = visible && vivant;
    if (enLigne !== annonce) {
      annonce = enLigne;
      surChangement(enLigne);
    }
  };

  const arrets = [
    onValue(ref(db(), paths.userField(uid, 'lastSeenAt')), (instantane) => {
      dernierVu = (instantane.val() as number | null) ?? null;
      juger();
    }),
    onValue(ref(db(), paths.userField(uid, 'connections')), (instantane) => {
      connexions = Object.keys((instantane.val() as object | null) ?? {}).length;
      juger();
    }),
    onValue(ref(db(), paths.userField(uid, 'presenceV2')), (instantane) => {
      v2 = instantane.val() === true;
      juger();
    }),
    onValue(ref(db(), paths.userField(uid, 'privacy/showOnlineStatus')), (instantane) => {
      // Absent vaut oui, comme dans DEFAULT_PRIVACY.
      visible = (instantane.val() as boolean | null) !== false;
      juger();
    }),
  ];
  // Le repli au battement vieillit sans que rien ne s'ecrive : on rejuge.
  const metronome = window.setInterval(juger, 30_000);

  return () => {
    for (const arret of arrets) arret();
    window.clearInterval(metronome);
  };
}

/** Reglages de visibilite. Le patch est partiel, l'ecrit est complet. */
export async function updatePrivacy(patch: Partial<PrivacySettings>): Promise<PrivacySettings> {
  const uid = requireUid();
  const record = await myRecord(uid);

  // Firebase refuse `undefined` : on ne retient que les reglages fournis,
  // les autres gardent leur valeur actuelle.
  const provided = Object.fromEntries(
    Object.entries(patch).filter(([, value]) => value !== undefined),
  ) as Partial<PrivacySettings>;

  const privacy: PrivacySettings = { ...DEFAULT_PRIVACY, ...record.privacy, ...provided };
  await writeValue(paths.userField(uid, 'privacy'), privacy);
  return privacy;
}

/** Ecran Reglages : compte, confidentialite et informations d'application. */
/**
 * Ce dont on accepte d'etre prevenu.
 *
 * Meme mecanique que `updatePrivacy` : on n'ecrit que ce qui est fourni,
 * le reste garde sa valeur, et un compte qui n'a jamais rien regle part
 * du defaut plutot que d'un objet vide.
 */
export async function updateNotifications(
  patch: Partial<NotificationSettings>,
): Promise<NotificationSettings> {
  const uid = requireUid();
  const record = await myRecord(uid);

  const provided = Object.fromEntries(
    Object.entries(patch).filter(([, value]) => value !== undefined),
  ) as Partial<NotificationSettings>;

  const notifications: NotificationSettings = {
    ...DEFAULT_NOTIFICATIONS,
    ...record.notifications,
    ...provided,
  };
  await writeValue(paths.userField(uid, 'notifications'), notifications);
  return notifications;
}

export async function settings(): Promise<SettingsResponse> {
  const uid = requireUid();
  const [record, blocked] = await Promise.all([myRecord(uid), listBlocked()]);
  return {
    username: record.username,
    email: record.email,
    currentServerId: record.currentServerId ?? null,
    privacy: record.privacy ?? DEFAULT_PRIVACY,
    /*
      Les defauts sous ce qui est ecrit, et non a la place.

      Un membre qui a deja touche un interrupteur porte un objet complet
      au jour ou il l'a touche. Ajouter un reglage plus tard le laissait
      donc absent chez lui : l'ecran lisait `undefined` et montrait
      l'interrupteur eteint, pendant que les fonctions — pour qui absent
      vaut oui — continuaient d'envoyer. Le reglage disait le contraire
      de ce qui se passait.
    */
    notifications: { ...DEFAULT_NOTIFICATIONS, ...record.notifications },
    blockedCount: blocked.length,
    appVersion: '1.0',
  };
}

/**
 * Recherche un membre par pseudo, dans tout l'annuaire.
 *
 * Distincte de `friends.searchUsers` : celle-ci sert a nouer une amitie
 * et renvoie l'etat de la relation, alors qu'ici on cherche seulement
 * quelqu'un a mettre dans un groupe — on n'est pas oblige d'etre ami
 * pour discuter a plusieurs.
 */
export async function searchMembers(search: string): Promise<PublicUser[]> {
  const uid = requireUid();
  const needle = normalizeUsername(search);
  if (!needle) return [];

  const [records, blocked] = await Promise.all([
    readList<UserRecord>(paths.users()),
    listBlocked(),
  ]);
  const muted = new Set(blocked);

  return records
    .filter(
      (record) =>
        record.id !== uid &&
        !muted.has(record.id) &&
        normalizeUsername(record.username ?? '').includes(needle),
    )
    .map((record) => toPublicUser(record.id, record))
    .sort((a, b) => a.username.localeCompare(b.username, 'fr'));
}

/* ------------------------------------------------------------------ */
/* Spots                                                               */
/* ------------------------------------------------------------------ */

/** Minuscules et sans accents : "Marrakech" doit repondre a "marrakech". */
function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Catalogue des spots, filtre par la barre de recherche.
 * Le catalogue tient en une poignee d'entrees : on le lit en entier et
 * on filtre en memoire, ce qui evite trois index cote base.
 */
/** Les textes d'un serveur, tels que la base les garde dans une autre langue. */
type TextesDeServeur = Partial<Pick<GeoServer, 'name' | 'shortName' | 'country' | 'region' | 'tagline'>>;
/** Une fiche et ses traductions, une branche par langue (`servers/{id}/en`, `/es`...). */
type FicheTraduite = Partial<Record<AutreLangue, TextesDeServeur>>;

/**
 * Un serveur dans la langue du membre.
 *
 * La fiche garde son francais, qui fait foi, et une version anglaise sous
 * `en` (voir `tools/traduire-serveurs.mjs`) : « Londres » devient
 * « London », « Royaume-Uni » « United Kingdom ». Un champ absent retombe
 * sur le francais.
 */
export function serveurDansLaLangue<S extends GeoServer>(server: S & FicheTraduite): S {
  const textes = langue === 'fr' ? undefined : server[langue];
  if (!textes) return server;
  const traduit = Object.fromEntries(Object.entries(textes).filter(([, valeur]) => Boolean(valeur)));
  return { ...server, ...traduit };
}

/** Les noms traduits d'une fiche, s'il y en a : ce qu'une destination en garde. */
function nomsTraduits(fiche: FicheTraduite): Partial<Record<AutreLangue, string>> | undefined {
  const noms = Object.fromEntries(
    AUTRES_LANGUES.flatMap((code) => (fiche[code]?.name ? [[code, fiche[code]!.name]] : [])),
  ) as Partial<Record<AutreLangue, string>>;
  return Object.keys(noms).length ? noms : undefined;
}

export async function listServers(search?: string): Promise<GeoServer[]> {
  const bruts = await readList<GeoServer & FicheTraduite>(paths.servers());
  const servers = bruts.map(serveurDansLaLangue);
  // Toute lecture du catalogue reapprovisionne les fuseaux : c'est la
  // seule facon pour la couche evenement de savoir quelle heure il est
  // dans une ville ou l'on n'est pas.
  for (const server of servers) retenirFuseau(server.id, server.timezone);
  const needle = normalize((search ?? '').trim());
  if (!needle) return servers;

  /* On cherche dans les deux langues : « Londres » trouve London, et l'inverse. */
  return servers.filter((server, rang) =>
    normalize(
      `${server.name} ${server.country} ${server.region} ${bruts[rang]?.name} ${bruts[rang]?.country}`,
    ).includes(needle),
  );
}

/** La fiche telle que la base la garde : le francais, et l'anglais sous `en`. */
async function lireServeur(serverId: string): Promise<GeoServer & FicheTraduite> {
  const server = await readValue<GeoServer & FicheTraduite>(paths.server(serverId));
  if (!server) throw new SiroccoDataError(t.profil.erreurs.spotIntrouvable, 'not-found');
  retenirFuseau(serverId, server.timezone);
  return { ...server, id: serverId };
}

export async function getServer(serverId: string): Promise<GeoServer> {
  return serveurDansLaLangue(await lireServeur(serverId));
}

/**
 * Les serveurs prives dont j'ai la cle, parmi ceux qu'on me donne.
 *
 * Une lecture par serveur prive : chacun ne lit que sa propre ligne, la
 * liste entiere ne regarde personne. Une ligne illisible vaut « pas de
 * cle » plutot qu'une erreur : le catalogue doit s'afficher quand meme.
 */
export async function mesAccesPrives(serverIds: string[]): Promise<Set<string>> {
  const uid = requireUid();
  const reponses = await Promise.all(
    serverIds.map(async (serverId) =>
      (await readOptionalValue<boolean>(paths.serveurPriveMembre(serverId, uid)).catch(() => null)) ===
      true
        ? serverId
        : null,
    ),
  );
  return new Set(reponses.filter((id): id is string => id !== null));
}

/**
 * Rejoint un spot : c'est ce qui repeint toute l'application.
 * On en profite pour ouvrir la destination, sinon l'ecran Profil
 * afficherait un membre sans aucune ville.
 */
export async function joinServer(serverId: string): Promise<CurrentUser> {
  const uid = requireUid();
  const [brut, record, trophyIds] = await Promise.all([
    lireServeur(serverId),
    myRecord(uid),
    readKeys(paths.trophies(serverId)),
  ]);

  const server = serveurDansLaLangue(brut);
  if (!server.available) {
    throw new SiroccoDataError(
      t.profil.erreurs.bientot(server.name),
      'server-unavailable',
    );
  }
  /* Les regles refuseraient de toute facon : on le dit en clair avant. */
  if (server.prive && !(await mesAccesPrives([serverId])).has(serverId)) {
    throw new SiroccoDataError(t.profil.erreurs.prive(server.name), 'server-private');
  }

  const existing = record.destinations?.[serverId];
  // Nom et couleur viennent toujours du catalogue : un spot renomme ne
  // doit pas laisser d'ancien libelle dans les profils.
  const destination: DestinationRecord = {
    /*
      Le nom dans toutes les langues, jamais celui de qui rejoint seulement :
      la fiche est lue par d'autres membres, chacun dans la sienne.
    */
    serverName: brut.name,
    ...(nomsTraduits(brut) ? { serverNames: nomsTraduits(brut) } : {}),
    points: existing?.points ?? 0,
    trophiesUnlocked: existing?.trophiesUnlocked ?? 0,
    trophiesTotal: trophyIds.length,
    accent: server.theme.primary,
    ...(server.sansTrophees ? { sansTrophees: true } : {}),
  };

  await updateValues({
    [paths.userField(uid, 'currentServerId')]: serverId,
    [paths.userField(uid, `destinations/${serverId}`)]: destination,
  });

  return toCurrentUser(uid, {
    ...record,
    currentServerId: serverId,
    destinations: { ...record.destinations, [serverId]: destination },
  });
}

/**
 * Supprime son compte, et tout ce qu'il a seme.
 *
 * Rien ne se fait ici : les regles laissent chacun ecrire sa propre
 * fiche, mais pas defaire le lien range chez un ami ni la demande
 * deposee dans sa boite, et effacer un compte d'authentification n'existe
 * que dans le SDK Admin. C'est donc la fonction `deleteAccount` qui
 * travaille, au-dessus des regles.
 *
 * La deconnexion qui suit n'est pas une politesse : le jeton du
 * telephone reste valable une heure apres que le compte a disparu, et
 * sans elle l'application continuerait de croire quelqu'un connecte.
 */
export async function deleteAccount(): Promise<void> {
  await appeler<Record<string, never>, { ok: boolean }>('deleteAccount', {});
  await logout();
}
